import React from 'react';
import { IBuckleStrapSpecification, IProduct } from '../../interfaces';
import { getProductSpecificationsByLang } from '../../utils/product';
import { useTranslation } from '../../i18n';
import { performSmoothScroll } from '../../utils/window';

interface Props {
  selectedBuckle: IProduct;
  buckles: IProduct[] | undefined;
  currentStrap: IProduct;
  onSelectBuckle: (buckle: IProduct) => void;
}

const BuckleSelector = ({
  selectedBuckle,
  buckles,
  currentStrap,
  onSelectBuckle
}: Props) => {
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'EN';

  const specs = getProductSpecificationsByLang(
    selectedBuckle,
    language
  ) as IBuckleStrapSpecification;

  const strapSpecs = getProductSpecificationsByLang(
    currentStrap,
    language
  ) as IBuckleStrapSpecification;

  const hasOptions = !!buckles?.length && buckles.length > 1;
  const buckleInfo = (
    <div className="info buckle-info">
      {selectedBuckle ? (
        <>
          {specs?.material && <span>{specs.material}</span>}
          {specs?.name && <span>{specs.name}</span>}
          {specs?.size && <span>{specs.size}</span>}
          <span className="modelCode">{selectedBuckle.modelCode}</span>
        </>
      ) : (
        <span className="main-info">{t('no_buckle')}</span>
      )}
    </div>
  );
  const strapInfo = (
    <>
      <div className={`info strap-info-line`} />
      <div className={`info strap-info`}>
        {strapSpecs?.material && <span>{strapSpecs.material}</span>}
        {strapSpecs?.color &&
          strapSpecs?.color?.toLowerCase() !==
            strapSpecs?.material?.toLowerCase() && (
            <span>{strapSpecs.color}</span>
          )}
        {strapSpecs?.size && <span>{strapSpecs.size}</span>}
        <span className="modelCode">{currentStrap?.modelCode}</span>
        <button
          onClick={() => performSmoothScroll('#strapSection')}
          className={`btn btn-sm btn-light mt-2 strap-select`}
        >
          {t('change_strap')}
        </button>
      </div>
    </>
  );
  return (
    <div className="BuckleSelector">
      <div className="BuckleSelector-inner">
        {buckleInfo}
        {hasOptions && (
          <div className="options">
            {buckles.map((buckle) => (
              <img
                onClick={() => {
                  onSelectBuckle(buckle);
                }}
                key={buckle.id}
                src={buckle.imageUrls[0]}
                className={`accessories-thumb ${
                  selectedBuckle.id === buckle.id
                }`}
              />
            ))}
          </div>
        )}

        <div
          className={`preview${!buckles?.length ? ' no-option' : ''}${
            buckles?.length === 1 ? ' single-option' : ''
          }`}
        >
          {selectedBuckle && (
            <img
              src={selectedBuckle.imageUrls[1]}
              className="buckle"
              alt="Buckle"
            />
          )}
          {buckleInfo}
          {strapInfo}

          <img src={currentStrap.imageUrls[2]} className="strap" alt="Strap" />
        </div>
      </div>

      <style jsx>{`
        .BuckleSelector {
          display: flex;
          flex-direction: column;
          margin: auto;
          overflow: hidden;
          align-items: center;
          padding: 50px 0;
        }
        .BuckleSelector :global(.info) {
          margin: auto;
          max-width: 200px;
        }
        .BuckleSelector :global(.info span) {
          display: block;
          font-size: 0.7em;
          text-transform: uppercase;
          letter-spacing: 1px;
          color: #666;
        }
        .BuckleSelector :global(.info span:first-child) {
          font-size: 0.7em;
          color: #000;
          font-weight: bold;
        }
        .preview-container {
          display: flex;
        }
        .options {
          text-align: center;
          max-width: 80%;
          margin: 20px auto 0;
          height: 100px;
          position: relative;
          z-index: 2;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .accessories-thumb {
          width: 50px;
          height: auto;
          background: #fff;
          border-radius: 100%;
          overflow: hidden;
          border: 2px solid #efefef;
          margin: 2px;
        }
        .accessories-thumb.true {
          opacity: 1;
          background: #fff;
          border-color: #333;
        }
        .preview {
          width: 350px;
          position: relative;
          margin: auto;
          height: 580px;
          margin-top: -50px;
        }
        .preview.no-option {
          margin-top: -110px;
        }
        .preview.single-option {
          margin-top: 0;
        }
        .preview img {
          width: 100%;
          height: auto;
        }

        .strap {
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          z-index: 2;
          top: ${350 / 2.1875}px;
        }
        .buckle {
          position: relative;
          z-index: 1;
        }
        .preview :global(.buckle-info),
        .preview :global(.strap-info),
        .preview :global(.strap-info-line) {
          display: none;
        }

        @media (max-width: 768px) {
          .BuckleSelector {
            padding-top: 10px;
          }
          .BuckleSelector-inner > :global(.buckle-info) {
            display: none;
          }
          .preview > :global(.info) {
            top: 50%;
            position: absolute;
          }

          .preview > :global(.buckle-info) {
            display: block;
            text-align: left;
            right: 50%;
            border-top: 1px solid #ddd;
            padding-right: 30px;
            width: 120px;
            margin-right: 40px;
            top: ${hasOptions ? 120 : 170}px;
          }

          .preview :global(.strap-select) {
            font-size: 0.7em;
            background: #ccc;
          }

          .preview :global(.strap-info),
          .preview :global(.strap-info-line) {
            display: block;
            text-align: right;
            margin-top: 20px;
            width: 160px;
            left: 50%;
          }

          .preview > :global(.strap-info-line) {
            border-top: 1px solid #ddd;
          }

          .preview > :global(.strap-info) {
            padding-left: 60px;
            z-index: 10;
          }

          .preview.no-option {
            margin-top: -70px;
          }
        }
      `}</style>
    </div>
  );
};

export default BuckleSelector;
